import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@/lib/api";

interface PromptRow {
  id: string;
  category: string;
  body: string;
  active: number;
  ai_suggested: number;
  created_at: number;
  participation_count: number;
}

/**
 * Quiet, collapsible archive of prior prompts for a circle. Defaults to
 * collapsed so the detail page stays focused on the active prompt — the
 * archive is for context, not a feed.
 *
 * Participation counts shown per prompt; never names, never bodies.
 */
export function PromptArchive({ circleId }: { circleId: string }) {
  const api = useApi();
  const [open, setOpen] = useState(false);

  const list = useQuery({
    queryKey: ["circle-prompts", circleId],
    queryFn: () => api.get<{ prompts: PromptRow[] }>(`/circles/${circleId}/prompts`),
    enabled: open, // don't fetch until expanded
  });

  // Past prompts only — current active prompt lives in the GuidedPrompt panel above.
  const past = (list.data?.prompts ?? []).filter((p) => p.active !== 1);

  return (
    <div className="panel p-6">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-left group"
      >
        <span>
          <span className="label-eyebrow">Archive</span>
          <span className="block mt-1 font-display text-base text-fog group-hover:text-gold-glow transition-colors">
            Past prompts
          </span>
        </span>
        <span className="text-[10px] uppercase tracking-[0.18em] text-whisper">
          {open ? "Hide" : "Open"}
        </span>
      </button>

      {open && (
        <div className="mt-6">
          {list.isLoading ? (
            <p className="text-sm text-whisper">Reading the archive…</p>
          ) : past.length === 0 ? (
            <p className="text-sm text-whisper italic leading-relaxed">
              No past prompts yet. A quiet week is not a failure.
            </p>
          ) : (
            <ul className="space-y-5 divide-y divide-veil/30 [&>li:not(:first-child)]:pt-5">
              {past.map((p) => (
                <li key={p.id}>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-muted">
                    {p.category} ·{" "}
                    {new Date(p.created_at).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                    {p.participation_count > 0 && (
                      <span>
                        {" · "}
                        {p.participation_count}{" "}
                        {p.participation_count === 1 ? "response" : "responses"}
                      </span>
                    )}
                  </p>
                  <p className="mt-2 text-sm text-fog/90 leading-relaxed">
                    {p.body}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
